import { useEffect, useState } from "react";
import { api, euro } from "@/lib/api";
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend, ResponsiveContainer
} from "recharts";
import { UsersThree } from "@phosphor-icons/react";
import { PeriodCompareCard } from "@/components/PeriodCompareCard";

const COLORS = ["#2d5a3d","#2f6fbf","#d97757","#e0a020","#8a5cf5","#5aa872"];
const nf = (n) => (n || 0).toLocaleString("de-DE");

export default function Statistics() {
  const [s, setS] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  useEffect(() => { api.get(`/stats/dashboard?year=${year}`).then(r=>setS(r.data)); }, [year]);
  if (!s) return <div className="text-muted-foreground">Lade…</div>;

  const totalYear = (s.monthly_revenue || []).reduce((a,b)=>a+b,0);
  const avgOcc = Math.round((s.occupancy_monthly || []).reduce((a,b)=>a+b,0)/12);

  const personsByYear = s.persons_by_year || {};
  const years = Object.keys(personsByYear).sort();
  const personsAll = s.total_persons_all_time || 0;

  return (
    <div className="space-y-6" data-testid="statistics-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>Umsatz & Statistik {year}</h1>
          <p className="text-muted-foreground mt-1">Detaillierte Auswertungen für Ihre Unterkunft</p>
        </div>
        {s.available_years && s.available_years.length > 0 && (
          <div className="flex gap-2" data-testid="stats-year-selector">
            {s.available_years.map(y => (
              <button key={y} onClick={()=>setYear(y)}
                      data-testid={`stats-year-btn-${y}`}
                      className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${
                        year === y ? "bg-primary text-white shadow-md" : "bg-muted text-foreground hover:bg-primary/10"
                      }`}>{y}</button>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="msb-card"><div className="text-xs uppercase text-muted-foreground tracking-widest">Umsatz Jahr</div><div className="text-2xl font-bold mt-2">{euro(totalYear)}</div></div>
        <div className="msb-card"><div className="text-xs uppercase text-muted-foreground tracking-widest">Ø Auslastung</div><div className="text-2xl font-bold mt-2">{avgOcc}%</div></div>
        <div className="msb-card"><div className="text-xs uppercase text-muted-foreground tracking-widest">Gebuchte Nächte</div><div className="text-2xl font-bold mt-2">{s.total_nights_year}</div></div>
        <div className="msb-card"><div className="text-xs uppercase text-muted-foreground tracking-widest">Offene Zahlungen</div><div className="text-2xl font-bold mt-2">{euro(s.open_payments)}</div></div>
      </div>

      <PeriodCompareCard />

      {/* Personen-Übernachtungen (Gäste, nicht Nächte) */}
      <div className="msb-card" data-testid="stats-persons-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-2xl bg-primary/10 flex items-center justify-center">
            <UsersThree size={22} weight="duotone" className="text-primary"/>
          </div>
          <div>
            <h3 className="text-lg font-semibold" style={{fontFamily:"'Work Sans',sans-serif"}}>Personen übernachtet</h3>
            <p className="text-xs text-muted-foreground">Summe der Personen pro Buchung · bei Gruppen- und Mehrzimmer-Buchungen jede Person einzeln gezählt.</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {years.map(y => (
            <div key={y}
                 className={`rounded-xl border p-4 ${String(y) === String(year) ? "bg-primary/5 border-primary/40" : "bg-slate-50 border-slate-200"}`}
                 data-testid={`persons-tile-${y}`}>
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-medium">{y}</div>
              <div className="text-3xl font-bold mt-1 text-foreground">{nf(personsByYear[y])}</div>
              <div className="text-[11px] text-muted-foreground mt-1">Personen</div>
            </div>
          ))}
          <div className="rounded-xl border-2 border-primary bg-primary/5 p-4" data-testid="persons-tile-all">
            <div className="text-xs uppercase tracking-widest text-primary font-semibold">All Time</div>
            <div className="text-3xl font-bold mt-1 text-primary">{nf(personsAll)}</div>
            <div className="text-[11px] text-primary/70 mt-1">Personen gesamt</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="msb-card">
          <h3 className="text-lg font-semibold mb-4" style={{fontFamily:"'Work Sans',sans-serif"}}>Buchungsquellen</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={s.by_source} dataKey="value" nameKey="name" outerRadius={100} innerRadius={55} label>
                {s.by_source.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
              </Pie>
              <Tooltip formatter={(v)=>euro(v)}/><Legend/>
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="msb-card" data-testid="stats-meals-card">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
            <div>
              <h3 className="text-lg font-semibold" style={{fontFamily:"'Work Sans',sans-serif"}}>Verpflegung · Frühstück & Abendessen</h3>
              <p className="text-xs text-muted-foreground">Aus allen Buchungen aggregiert · basiert auf den Extras-/Portions-Zählern.</p>
            </div>
            <MealsYearSelector meals_by_year={s.meals_by_year} defaultYear={year} allTime={s.meals_all_time}/>
          </div>
        </div>
      </div>
    </div>
  );
}

function MealsYearSelector({ meals_by_year, defaultYear, allTime }) {
  const years = Object.keys(meals_by_year || {}).sort();
  const [scope, setScope] = useState(String(defaultYear));

  const isAll = scope === "all";
  const data  = isAll ? (allTime || {breakfast:0,dinner:0,total:0}) : (meals_by_year?.[scope] || {breakfast:0,dinner:0,total:0});
  const label = isAll ? "· Gesamt (All Time)" : ` ${scope}`;

  return (
    <div className="w-full">
      <div className="flex flex-wrap gap-1.5 justify-end mb-3" data-testid="meals-year-selector">
        {years.map(y => (
          <button key={y} onClick={() => setScope(y)} data-testid={`meals-year-${y}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
              scope === y ? "bg-primary text-white shadow-md" : "bg-muted text-foreground hover:bg-primary/10"
            }`}>{y}</button>
        ))}
        <button onClick={() => setScope("all")} data-testid="meals-year-all"
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
            isAll ? "bg-primary text-white shadow-md" : "bg-muted text-foreground hover:bg-primary/10"
          }`}>All&nbsp;Time</button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <MealTile label={`Frühstück${label}`}       value={data.breakfast || 0} icon="🥐" tint="amber"/>
        <MealTile label={`Abendessen${label}`}      value={data.dinner    || 0} icon="🍽️" tint="red"/>
        <MealTile label={`Portionen gesamt${label}`} value={data.total    || 0} icon="🍴" tint="green" bold/>
      </div>

      {years.length > 1 && (
        <div className="pt-4 mt-4 border-t">
          <div className="text-xs uppercase text-muted-foreground tracking-widest mb-2">Verlauf nach Jahr</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={years.map(y => ({name: y, Frühstück: meals_by_year[y].breakfast||0, Abendessen: meals_by_year[y].dinner||0}))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb"/>
              <XAxis dataKey="name" fontSize={12}/><YAxis fontSize={12}/>
              <Tooltip/><Legend/>
              <Bar dataKey="Frühstück" fill="#d97757" radius={[6,6,0,0]}/>
              <Bar dataKey="Abendessen" fill="#8a5cf5" radius={[6,6,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function MealTile({ label, value, icon, tint = "slate", bold = false }) {
  const tintMap = {
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    red:   "bg-red-50 border-red-200 text-red-900",
    green: "bg-emerald-50 border-emerald-200 text-emerald-900",
    slate: "bg-slate-50 border-slate-200 text-slate-800",
  };
  return (
    <div className={`rounded-xl border p-4 ${tintMap[tint] || tintMap.slate}`} data-testid={`meal-tile-${label}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest opacity-80">
        <span className="text-base">{icon}</span>
        <span className="font-medium">{label}</span>
      </div>
      <div className={`mt-2 ${bold ? "text-3xl" : "text-2xl"} font-bold`}>{value.toLocaleString("de-DE")}</div>
    </div>
  );
}
